import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { AuraMark } from "@/components/AuraMark";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Grid3x3, ArrowLeft, Lock, Unlock, Plus, Play, Sparkles, Wand2, Loader2, Coins, Trash2, Moon, Sun, Shuffle, Minus, Zap, Music2, Square, Repeat, Volume2, VolumeX, Send } from "lucide-react";

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

// Single shared AudioContext + master gain, reused for every preview. Creating one per
// click breaks on mobile Safari (each starts suspended and iOS caps the number of contexts).
let _ctx = null, _master = null, _loopTimer = null, _live = [];
function _ac() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_ctx || _ctx.state === "closed") {
    _ctx = new AC();
    _master = _ctx.createGain();
    _master.gain.value = 0.9;
    _master.connect(_ctx.destination);
  }
  return _ctx;
}

function setPreviewVolume(v) {
  if (_ctx && _master) _master.gain.setTargetAtTime(Math.max(0, Math.min(1, v)), _ctx.currentTime, 0.015);
}

function stopPreview() {
  if (_loopTimer) { clearInterval(_loopTimer); _loopTimer = null; }
  _live.forEach((o) => { try { o.stop(); } catch (e) { /* noop */ } });
  _live = [];
}

function _schedule(clip, when) {
  const spb = 60 / clip.tempo / 4; // seconds per 16th
  const bars = Math.max(1, clip.bars || 1);
  if (clip.role === "drums" && clip.drum_grid) {
    const g = clip.drum_grid;
    for (let bar = 0; bar < bars; bar++) {
      ["kick", "snare", "hat"].forEach((k) => (g[k] || []).forEach((on, i) => {
        if (!on) return;
        const t = when + (bar * 16 + i) * spb;
        const o = _ctx.createOscillator(), gn = _ctx.createGain();
        o.frequency.value = k === "kick" ? 90 : k === "snare" ? 220 : 8000;
        o.type = k === "hat" ? "square" : "sine";
        gn.gain.setValueAtTime(k === "hat" ? 0.12 : 0.3, t);
        gn.gain.exponentialRampToValueAtTime(0.001, t + (k === "kick" ? 0.18 : 0.08));
        o.connect(gn).connect(_master); o.start(t); o.stop(t + 0.2); _live.push(o);
      }));
    }
  } else {
    (clip.notes || []).forEach((n) => {
      const t = when + n.step * spb;
      const o = _ctx.createOscillator(), gn = _ctx.createGain();
      o.frequency.value = 440 * Math.pow(2, (n.midi - 69) / 12);
      o.type = "triangle";
      gn.gain.setValueAtTime(0.0001, t);
      gn.gain.exponentialRampToValueAtTime(0.22, t + 0.01);
      gn.gain.exponentialRampToValueAtTime(0.001, t + n.dur * spb);
      o.connect(gn).connect(_master); o.start(t); o.stop(t + n.dur * spb + 0.05); _live.push(o);
    });
  }
  return bars * 16 * spb; // seconds
}

async function playClip(clip, { loop = false } = {}) {
  const ctx = _ac();
  if (!ctx) return { ok: false, reason: "unsupported" };
  // Must resume inside the user gesture, especially on iOS/Android.
  try { if (ctx.state !== "running") await ctx.resume(); } catch (e) { /* noop */ }
  stopPreview();
  const durSec = _schedule(clip, ctx.currentTime + 0.06);
  if (loop) {
    _loopTimer = setInterval(() => { _live = []; _schedule(clip, _ctx.currentTime + 0.02); }, Math.max(300, durSec * 1000));
  }
  return { ok: ctx.state === "running", durMs: durSec * 1000, reason: ctx.state !== "running" ? "muted" : null };
}

const ClipGrid = ({ clip }) => {
  const cols = clip.bars * 16;
  if (clip.role === "drums" && clip.drum_grid) {
    const rows = [["Hat", clip.drum_grid.hat], ["Snare", clip.drum_grid.snare], ["Kick", clip.drum_grid.kick]];
    return (
      <div className="space-y-1">
        {rows.map(([lbl, arr]) => (
          <div key={lbl} className="flex items-center gap-1">
            <span className="w-9 text-[9px] text-slate-500 font-mono-a">{lbl}</span>
            <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(16, 1fr)`, gap: 2 }}>
              {(arr || []).map((on, i) => (
                <div key={i} className="h-4 rounded-sm" style={{ background: on ? "#C8A15C" : "rgba(255,255,255,0.05)", opacity: i % 4 === 0 ? 1 : 0.75 }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }
  const midis = (clip.notes || []).map((n) => n.midi);
  const lo = Math.min(...midis, 60), hi = Math.max(...midis, 72);
  const rowsN = [];
  for (let m = hi; m >= lo; m--) rowsN.push(m);
  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: cols * 10 }}>
        {rowsN.slice(0, 16).map((m) => (
          <div key={m} className="flex items-center gap-1 h-3.5">
            <span className="w-7 text-[8px] text-slate-600 font-mono-a">{NOTE_NAMES[m % 12]}{Math.floor(m / 12) - 1}</span>
            <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 1 }}>
              {Array.from({ length: cols }).map((_, s) => {
                const note = (clip.notes || []).find((n) => n.midi === m && s >= n.step && s < n.step + n.dur);
                return <div key={s} className="h-3 rounded-[2px]" style={{ background: note ? "#7ad0e0" : (s % 16 === 0 ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.03)") }} />;
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ClipLab() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [clips, setClips] = useState([]);
  const [gen, setGen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [role, setRole] = useState("melody");
  const [key, setKey] = useState("A");
  const [scale, setScale] = useState("minor");
  const [tempo, setTempo] = useState(120);
  const [bars, setBars] = useState(4);
  const [complexity, setComplexity] = useState(5);
  const [energy, setEnergy] = useState(5);
  const [mode, setMode] = useState("8-bar idea");
  const [playingId, setPlayingId] = useState(null);
  const [mutedHinted, setMutedHinted] = useState(false);
  const [loop, setLoop] = useState(false);
  const [volume, setVolume] = useState(90);
  const [muted, setMuted] = useState(false);
  const [devices, setDevices] = useState([]);
  const connectedDevice = devices.find((d) => d.status === "connected");
  const playTimer = useRef(null);

  const changeVol = (v) => { setVolume(v); if (!muted) setPreviewVolume(v / 100); };
  const toggleMute = () => { const m = !muted; setMuted(m); setPreviewVolume(m ? 0 : volume / 100); };
  const stopAll = () => { if (playTimer.current) { clearTimeout(playTimer.current); playTimer.current = null; } stopPreview(); setPlayingId(null); };

  const preview = async (clip) => {
    const empty = (clip.role === "drums")
      ? !["kick", "snare", "hat"].some((k) => (clip.drum_grid?.[k] || []).some(Boolean))
      : !(clip.notes || []).length;
    if (empty) { toast.error("This clip has no notes to play"); return; }
    if (playTimer.current) { clearTimeout(playTimer.current); playTimer.current = null; }
    setPreviewVolume(muted ? 0 : volume / 100);
    const res = await playClip(clip, { loop });
    if (!res.ok && res.reason === "unsupported") { toast.error("Audio isn't supported in this browser"); return; }
    if (!res.ok && res.reason === "muted" && !mutedHinted) {
      toast.message("If you hear nothing, turn off your phone's silent/ring switch", { description: "Previews use your device audio." });
      setMutedHinted(true);
    }
    setPlayingId(clip.id);
    if (!loop) playTimer.current = setTimeout(() => setPlayingId((p) => (p === clip.id ? null : p)), Math.min(res.durMs || 1500, 8000));
  };

  const sendToDaw = async (cid) => {
    if (!connectedDevice) { toast.error("Pair a DAW under Devices first"); return; }
    try {
      const r = await api.post(`/clips/${cid}/send-to-daw`, { device_id: connectedDevice.id });
      toast.success(`Sent to ${String(r.data.daw || "").toUpperCase()}`, { description: "Drop it on a track with your pack loaded and press play." });
    } catch (e) {
      toast.error("Could not reach your DAW agent. Is the Aura Agent running?");
    }
  };

  const load = useCallback(() => api.get(`/projects/${pid}/clips`).then((r) => setClips(r.data)).catch(() => {}), [pid]);
  useEffect(() => {
    load();
    api.get(`/devices`).then((r) => setDevices(r.data || [])).catch(() => {});
    api.post(`/projects/${pid}/clip-lab/quote`, { role, bars }).then((r) => setQuote(r.data)).catch(() => {});
  }, [pid, load, role, bars]);
  useEffect(() => () => stopPreview(), []);

  const params = () => ({ role, key, scale, tempo, bars, complexity, energy, label: mode, mode });

  const create = async () => {
    setGen(true);
    try {
      const r = await api.post(`/projects/${pid}/clip-lab/generate`, params());
      setClips((c) => [...r.data.variations, ...c]);
      toast.success("3 ideas generated");
    } catch (e) {
      toast.error(e?.response?.status === 402 ? "Subscribe or add credits to generate" : "Generation failed");
    } finally { setGen(false); }
  };

  const variation = async (cid, action) => {
    try { const r = await api.post(`/clips/${cid}/variation`, { action }); setClips((c) => c.map((x) => x.id === cid ? r.data : x)); toast.success(action); }
    catch (e) { toast.error(e?.response?.status === 402 ? "Not enough credits" : "Failed"); }
  };
  const lock = async (cid, locked) => { await api.post(`/clips/${cid}/lock`, { locked }); setClips((c) => c.map((x) => x.id === cid ? { ...x, locked } : x)); };
  const addArr = async (cid) => { await api.post(`/clips/${cid}/add-to-arrangement`); setClips((c) => c.map((x) => x.id === cid ? { ...x, in_arrangement: true } : x)); toast.success("Added to arrangement"); };
  const del = async (cid) => { await api.delete(`/clips/${cid}`); setClips((c) => c.filter((x) => x.id !== cid)); };

  const QC = [["8-bar idea", "melody"], ["Drum bounce", "drums"], ["Bass / 808", "bass"], ["Chord bed", "harmony"], ["Lead motif", "lead"]];

  return (
    <div className="min-h-screen" style={{ background: "var(--daw-bg)" }}>
      <div className="flex">
        {/* sidebar */}
        <aside className="hidden lg:block w-56 shrink-0 border-r min-h-screen" style={{ borderColor: "var(--daw-border)", background: "var(--daw-panel)" }}>
          <button onClick={() => nav("/dashboard")} className="flex items-center gap-2.5 px-5 h-[68px]" data-testid="clip-logo"><AuraMark className="w-8 h-8" /><span className="font-display font-semibold text-slate-100">AuraSession</span></button>
          <div className="px-3 py-4 space-y-1">
            <button onClick={() => nav(`/studio/${pid}`)} data-testid="clip-back-studio" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-white/5 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Create</button>
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-100 bg-white/[0.06] text-sm"><Grid3x3 className="w-4 h-4 text-gold" /> Clip Lab</div>
          </div>
        </aside>

        {/* main */}
        <main className="flex-1 min-w-0">
          <div className="flex items-center justify-between px-4 sm:px-6 h-[68px] border-b gap-3" style={{ borderColor: "var(--daw-border)" }}>
            <div className="flex items-center gap-2 min-w-0">
              <button onClick={() => nav(`/studio/${pid}`)} data-testid="clip-mobile-back" className="lg:hidden p-1.5 -ml-1 rounded-md text-slate-400 hover:text-slate-100 shrink-0"><ArrowLeft className="w-5 h-5" /></button>
              <div className="min-w-0">
                <p className="text-[10px] tracking-[0.2em] uppercase text-gold font-mono-a">Aura Clip Lab</p>
                <h1 className="font-display font-semibold text-slate-50 text-base sm:text-lg leading-none mt-1 truncate">Idea fast. Lock what's fire.</h1>
              </div>
            </div>
            {quote && <div className="flex items-center gap-2 text-sm bg-black/30 border rounded-full px-3 py-1.5" style={{ borderColor: "var(--daw-border)" }} data-testid="clip-credits"><Coins className="w-4 h-4 text-gold" /><span className="text-slate-300">{quote.privileged ? "Unlimited" : `${quote.credits_available} credits`}</span></div>}
          </div>

          <div className="p-4 sm:p-6 grid lg:grid-cols-[300px_1fr] gap-6">
            {/* controls */}
            <div className="card-surface rounded-2xl p-5 space-y-4 h-fit min-w-0" style={{ background: "var(--daw-panel-2)" }}>
              <p className="text-slate-100 font-medium flex items-center gap-2"><Wand2 className="w-4 h-4 text-cyan" /> Quick Create</p>
              <div className="grid grid-cols-2 gap-2">
                {QC.map(([m, r]) => (
                  <button key={m} data-testid={`qc-${r}`} onClick={() => { setMode(m); setRole(r); }} className={`text-xs rounded-lg px-2 py-2 border transition-colors ${mode === m ? "border-gold text-gold bg-gold/10" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>{m}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] text-slate-500 font-mono-a">KEY</label>
                  <Select value={key} onValueChange={setKey}><SelectTrigger data-testid="clip-key" className="mt-1 h-9 bg-white/5 border-white/10"><SelectValue /></SelectTrigger><SelectContent className="card-surface border-white/10 max-h-52">{NOTE_NAMES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><label className="text-[10px] text-slate-500 font-mono-a">SCALE</label>
                  <Select value={scale} onValueChange={setScale}><SelectTrigger data-testid="clip-scale" className="mt-1 h-9 bg-white/5 border-white/10"><SelectValue /></SelectTrigger><SelectContent className="card-surface border-white/10">{["minor", "major", "dorian", "phrygian", "harmonic_minor"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <div><label className="text-[10px] text-slate-500 font-mono-a">TEMPO {tempo} BPM</label><Slider data-testid="clip-tempo" value={[tempo]} onValueChange={([v]) => setTempo(v)} min={60} max={200} step={1} className="mt-2" /></div>
              <div><label className="text-[10px] text-slate-500 font-mono-a">BARS {bars}</label><Slider value={[bars]} onValueChange={([v]) => setBars(v)} min={2} max={16} step={2} className="mt-2" /></div>
              <div><label className="text-[10px] text-slate-500 font-mono-a">COMPLEXITY {complexity}</label><Slider value={[complexity]} onValueChange={([v]) => setComplexity(v)} min={1} max={10} step={1} className="mt-2" /></div>
              <div><label className="text-[10px] text-slate-500 font-mono-a">ENERGY {energy}</label><Slider value={[energy]} onValueChange={([v]) => setEnergy(v)} min={1} max={10} step={1} className="mt-2" /></div>
              {quote && !quote.privileged && <p className="text-xs text-slate-500">Quote: <span className="text-gold">{quote.credits_required} credits</span> · 3 real MIDI variations</p>}
              <Button data-testid="clip-generate-btn" onClick={create} disabled={gen} className="w-full rounded-full btn-gold font-semibold h-11">{gen ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Generate ideas</>}</Button>
            </div>

            {/* clips */}
            <div className="min-w-0">
              {clips.length > 0 && (
                <div className="flex items-center gap-3 mb-4 card-surface rounded-xl px-4 py-2.5 flex-wrap" style={{ background: "var(--daw-panel-2)" }} data-testid="clip-transport">
                  <button data-testid="clip-loop-toggle" onClick={() => setLoop((l) => !l)} className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${loop ? "border-gold text-gold bg-gold/10" : "border-white/10 text-slate-400 hover:text-slate-200"}`} title="Loop preview"><Repeat className="w-3.5 h-3.5" /> Loop</button>
                  <button data-testid="clip-stop-btn" onClick={stopAll} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-white/10 text-slate-400 hover:text-red-300 transition-colors" title="Stop preview"><Square className="w-3.5 h-3.5" /> Stop</button>
                  <div className="flex items-center gap-2 flex-1 min-w-[140px] max-w-[220px]">
                    <button data-testid="clip-mute-btn" onClick={toggleMute} className="text-slate-400 hover:text-slate-100" title={muted ? "Unmute" : "Mute"}>{muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}</button>
                    <Slider data-testid="clip-volume" value={[muted ? 0 : volume]} onValueChange={([v]) => changeVol(v)} min={0} max={100} step={1} className="flex-1" />
                  </div>
                  {connectedDevice
                    ? <span className="text-[10px] text-slate-500 ml-auto hidden sm:block">DAW: <span className="text-gold">{String(connectedDevice.daw || "").toUpperCase()}</span> connected</span>
                    : <button data-testid="clip-pair-daw" onClick={() => nav("/profile")} className="text-[10px] text-slate-500 hover:text-gold ml-auto">Pair a DAW →</button>}
                </div>
              )}
              {clips.length === 0 ? (
                <div className="card-surface rounded-2xl p-12 text-center text-slate-500" style={{ background: "var(--daw-panel-2)" }}>
                  <Music2 className="w-8 h-8 mx-auto mb-3 text-slate-600" />
                  No clips yet. Dial in a role and hit <span className="text-gold">Generate ideas</span> for real, playable MIDI.
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4 min-w-0" data-testid="clips-grid">
                  {clips.map((c) => (
                    <motion.div key={c.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid={`clip-${c.id}`}
                      className="card-surface rounded-2xl p-4 min-w-0" style={{ background: "var(--daw-panel-2)", borderColor: c.locked ? "#C8A15C" : undefined }}>
                      <div className="flex items-center justify-between mb-3">
                        <div><p className="text-slate-100 text-sm font-medium">{c.label}</p><p className="text-[10px] text-slate-500 font-mono-a">{c.role} · {c.key} {c.scale} · {c.tempo}bpm · {c.bars}bars · v{c.version}</p></div>
                        <div className="flex items-center gap-1">
                          <button data-testid={`clip-play-${c.id}`} onClick={() => preview(c)} className={`p-1.5 rounded-md hover:bg-white/5 ${playingId === c.id ? "text-cyan bg-white/5" : "text-slate-400 hover:text-cyan"}`} title="Preview">{playingId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}</button>
                          <button data-testid={`clip-send-${c.id}`} onClick={() => sendToDaw(c.id)} disabled={!connectedDevice} className="p-1.5 rounded-md hover:bg-white/5 text-slate-400 hover:text-gold disabled:opacity-30" title={connectedDevice ? `Send to ${String(connectedDevice.daw || "").toUpperCase()}` : "Pair a DAW to send"}><Send className="w-4 h-4" /></button>
                          <button data-testid={`clip-lock-${c.id}`} onClick={() => lock(c.id, !c.locked)} className={`p-1.5 rounded-md hover:bg-white/5 ${c.locked ? "text-gold" : "text-slate-400 hover:text-slate-200"}`} title="Lock">{c.locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}</button>
                          <button data-testid={`clip-del-${c.id}`} onClick={() => del(c.id)} className="p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <div className="bg-black/30 rounded-lg p-2 mb-3"><ClipGrid clip={c} /></div>
                      <div className="flex flex-wrap gap-1.5">
                        {[["darker", Moon], ["brighter", Sun], ["rhythm", Shuffle], ["simplify", Minus], ["bounce", Zap], ["rewrite", Wand2]].map(([a, I]) => (
                          <button key={a} data-testid={`clip-${a}-${c.id}`} onClick={() => variation(c.id, a)} disabled={c.locked} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md border border-white/10 text-slate-400 hover:text-slate-100 disabled:opacity-40"><I className="w-3 h-3" /> {a}</button>
                        ))}
                      </div>
                      <Button data-testid={`clip-add-${c.id}`} onClick={() => addArr(c.id)} disabled={c.in_arrangement} className="w-full mt-3 rounded-full bg-white/10 hover:bg-white/15 text-sm h-9">{c.in_arrangement ? "In arrangement ✓" : <><Plus className="w-4 h-4 mr-1" /> Add to arrangement</>}</Button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
